"use client"

import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { MapPin, Search, X, Loader2, Plus, ChevronDown, Check } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { GRAIN_URL } from "@/components/public-nav-glass"
import { SEARCH_BG, searchBgAlpha } from "./canvas"
import { cn } from "@/lib/utils"
import { createClient } from "@/lib/supabase/client"
import {
  parseWorkQuery,
  filterShopsForWork,
  facetsActive,
  listingCityLabel,
  listingPlanTypes,
  listingMonthly,
  EMPTY_FACETS,
  PLAN_LABEL,
  POSITION_LABEL,
  COMPENSATION_LABEL,
  SPECIALTY_LABEL,
  type WorkFacets,
  type WorkJob,
  type WorkSearchListing,
} from "@/lib/work-search"
import { shopTypes } from "@/data/filters"
import { useUserRole } from "@/providers/user-role-provider"
import { useUiSounds } from "@/hooks/use-ui-sounds"
import { useRotator } from "@/hooks/use-rotator"

const TYPE_LABELS: Record<string, string> = Object.fromEntries(shopTypes.map((t) => [t.value, t.label]))

// Cities, not example queries — city is the one input that reliably
// returns something for every visitor, so the suggestions match exactly
// what the placeholder now asks for ("Search by city, state").
// Real cities with real shops in BOOTH right now — not aspirational
// examples. Miami is going live tonight (3 shops); CDMX already has two
// and its alias mapping (lib/format-location.ts) makes "CDMX" match the
// stored "Ciudad de México" listings. Brooklyn, not "NYC"/"New York" — it's
// the shop's actual stored city, and "nyc" isn't a recognized alias
// anywhere, so that chip would've returned nothing.
const EXAMPLES = ["Chicago, IL", "Brooklyn, NY", "Miami, FL", "CDMX"]

// The headline itself rotates through a few framings of the same pitch —
// a barber looking for their next chair reads any one of these the same
// way, so cycling them keeps the hero from going stale on a repeat visit.
const HEADLINES = [
  "Find a barbershop to work at",
  "Search for a shop across town",
  "Find another barbershop to work at",
  "Search for a shop that fits your style",
  "Change locations",
  "Expand your reach",
  "Your second shop is ready for you",
  "New setting, new clientele",
  "Your next chair is out there",
]
// Two rotators on one screen, choreographed rather than clocked.
//
// v1 shared one fast 1800ms clock, offset by half a beat — the tick-tock
// that reads as mechanical. v2 gave each its own interval so they'd drift;
// that just reads as random. v3 is a call and response: one slow bar
// (BEAT_MS), the headline changes on the downbeat, the placeholder answers
// on the half-beat, and every swap is an exit THEN an entrance — the old
// line drifts off to the right and fades before the new one drifts in from
// the left — so a change is a breath, not a cut.
const BEAT_MS = 5200

// Everything the search box actually matches on (lib/listing-search.ts):
// a shop's name, its city, state, country, and zip all hit.
const PLACEHOLDER_TEXTS = [
  "Search by shop name",
  "Search by city",
  "Search by state",
  "Search by country",
  "Search by zip code",
]

// The motion lives in globals.css (.search-text-in / .search-text-out):
// sideways rather than up — the headline and the placeholder sit stacked,
// and two things rising in the same column read as one loop; a lateral
// drift reads as the words arriving, and leaving.

function priceLine(listing: WorkSearchListing): string | null {
  if (listing.hide_rates) return "Contact for pricing"
  if (typeof listing.monthlyPrice === "number" && listing.monthlyPrice > 0) return `$${listing.monthlyPrice}/mo`
  if (typeof listing.weeklyPrice === "number" && listing.weeklyPrice > 0) return `$${listing.weeklyPrice}/wk`
  return null
}

// One line per fact, in the order a barber weighs them: what's open, how
// the chair is paid for, and — when the shop went to the trouble of posting
// — the role itself. Text under the image, not more badges on it: the
// listing page is where the detail lives, the card only has to say enough
// to earn the tap.
function HiringLines({ listing }: { listing: WorkSearchListing }) {
  const open = listing.availableBooths ?? 0
  const plans = listingPlanTypes(listing).map((t) => PLAN_LABEL[t])
  const job = listing.jobs?.[0]
  const more = (listing.jobs?.length ?? 0) - 1

  // A full shop says so, rather than going quiet: the card is still a way
  // to reach the owner about the next chair.
  const chairLine = [
    open > 0 ? `${open} chair${open === 1 ? "" : "s"} open` : "No open chairs right now · message the owner",
    plans.length ? plans.join(" · ") : null,
  ]
    .filter(Boolean)
    .join(" · ")

  // Title · how it's paid · the schedule (two, then "+N") · what they cut.
  // A shop that ticked every schedule box used to run the line off the
  // card before the pay showed — and pay is the fact a barber reads first.
  const positions = (job?.position_type ?? []).map((p) => POSITION_LABEL[p] ?? p)
  const positionLine =
    positions.length > 2 ? `${positions.slice(0, 2).join(", ")} +${positions.length - 2}` : positions.join(", ")
  const jobLine = job
    ? [
        job.title,
        job.compensation ? COMPENSATION_LABEL[job.compensation] ?? job.compensation : null,
        positionLine || null,
        (job.specialties ?? []).length ? (job.specialties ?? []).slice(0, 3).map((sp) => SPECIALTY_LABEL[sp] ?? sp).join(", ") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null

  return (
    <div className="mt-2.5 space-y-1 px-0.5">
      {chairLine && <p className="truncate text-[11px] text-neutral-400">{chairLine}</p>}
      {jobLine && (
        <p className="truncate text-[11px] text-white">
          <span className="text-neutral-500">Hiring</span> · {jobLine}
          {more > 0 && <span className="text-neutral-500"> · +{more} more</span>}
        </p>
      )}
    </div>
  )
}

function ResultCard({ listing }: { listing: WorkSearchListing }) {
  const typeLabel = TYPE_LABELS[(listing.shopType || "barbershop").toLowerCase()] || listing.shopType
  const price = priceLine(listing)
  const isContactPrice = Boolean(listing.hide_rates)

  return (
    <Link href={`/listings/${listing.slug || listing.id}`} className="group block">
      <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-neutral-900">
        <Image
          src={listing.cover_image || "/classic-barbershop.png"}
          alt={listing.name}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />

        <span className="absolute left-2.5 top-2.5 rounded-full bg-white px-2 py-1 text-[11px] font-medium capitalize leading-none text-black">
          {typeLabel}
        </span>

        <div className="absolute inset-x-0 bottom-0 p-3">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-xs font-semibold leading-tight text-white">{listing.name}</h3>
              <p className="mt-0.5 flex items-center gap-1 text-[11px] text-white/70">
                <MapPin className="size-3 shrink-0" />
                <span className="truncate">{listingCityLabel(listing)}</span>
              </p>
            </div>
            {price && (
              <span
                className={cn(
                  "shrink-0 text-white",
                  isContactPrice ? "text-[11px] font-medium text-white/70" : "text-sm font-bold",
                )}
              >
                {price}
              </span>
            )}
          </div>
        </div>
      </div>
      <HiringLines listing={listing} />
    </Link>
  )
}

// ── Chips ───────────────────────────────────────────────────────────────
// The dashboard's Withdrawal / Request pair, sized as chips: the dark
// gradient pill with no stroke and a soft shadow is the resting state, and
// its white counterpart is hover — and, for a facet, selected. One chip for
// the example cities and every facet, so the page has one control.
const CHIP_DARK: React.CSSProperties = {
  background: "linear-gradient(to bottom, rgba(38,38,42,0.9), rgba(0,0,0,0.95))",
}
// The closing cards: the same dark gradient as the chips, no stroke, so they
// read as the Withdraw pill's family rather than a hairline box.
const CLOSE_CARD: React.CSSProperties = {
  background: "linear-gradient(to bottom, rgba(28,28,31,0.9), rgba(8,8,8,0.95))",
}
const CHIP_CLASS =
  "inline-flex h-7 items-center rounded-full px-3 text-xs font-normal shadow-sm transition-colors md:h-6 md:px-2.5 md:text-[11px]"

function Chip({
  active = false,
  onClick,
  className,
  children,
}: {
  active?: boolean
  onClick: () => void
  className?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(CHIP_CLASS, active ? "bg-white text-black" : "text-white hover:bg-white hover:text-black", className)}
      style={active ? undefined : CHIP_DARK}
      // Inline gradient beats a hover utility, so drop it on hover by hand.
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "" }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = CHIP_DARK.background as string }}
    >
      {children}
    </button>
  )
}


// Sort, as a dropdown on the right of the results line. It used to filter
// by rent bucket, but a barber comparing shops wants the cheap ones (or the
// new ones) first, not three of four buckets hidden. Recommended keeps the
// hiring-first ranking from filterShopsForWork. Same house chip as trigger
// and the round black-glass panel.
type SortKey = "recommended" | "price-asc" | "price-desc" | "newest"

const SORT_LABEL: Record<SortKey, string> = {
  recommended: "Recommended",
  "price-asc": "Price: low to high",
  "price-desc": "Price: high to low",
  newest: "Newest",
}
const SORT_OPTIONS = Object.keys(SORT_LABEL) as SortKey[]

function sortResults(results: WorkSearchListing[], sort: SortKey): WorkSearchListing[] {
  if (sort === "recommended") return results
  if (sort === "newest") {
    const t = (l: WorkSearchListing) => (l.created_at ? Date.parse(l.created_at) || 0 : 0)
    return [...results].sort((a, b) => t(b) - t(a))
  }
  // Hidden or unset rent can't be ranked by price, so it goes last either way.
  const dir = sort === "price-asc" ? 1 : -1
  return [...results].sort((a, b) => {
    const pa = listingMonthly(a)
    const pb = listingMonthly(b)
    if (pa === null && pb === null) return 0
    if (pa === null) return 1
    if (pb === null) return -1
    return (pa - pb) * dir
  })
}

function SortMenu({ sort, onChange }: { sort: SortKey; onChange: (next: SortKey) => void }) {
  // Trigger reads as the control's name until a non-default order is picked.
  const active = sort !== "recommended"
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(CHIP_CLASS, "gap-1.5 outline-none", active ? "bg-white text-black" : "text-white")}
          style={active ? undefined : CHIP_DARK}
        >
          {active ? SORT_LABEL[sort] : "Sort"}
          <ChevronDown className="size-3 opacity-70" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="min-w-[11rem] rounded-[22px] border-0 p-1.5 text-white shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
        style={{ background: "linear-gradient(to bottom, rgba(38,38,42,0.96), rgba(8,8,8,0.98))", boxShadow: "inset 0 0 0 0.75px #323232, 0 12px 40px rgba(0,0,0,0.6)" }}
      >
        {SORT_OPTIONS.map((k) => {
          const selected = sort === k
          return (
            <button
              key={k}
              type="button"
              onClick={() => onChange(k)}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-full px-3 py-1.5 text-left text-xs transition-colors",
                selected ? "bg-white text-black" : "text-neutral-300 hover:bg-white/10 hover:text-white",
              )}
            >
              {SORT_LABEL[k]}
              {selected && <Check className="size-3" strokeWidth={2.5} />}
            </button>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The header's two pills, for phones: centred under the city chips instead
 * of pinned to the top corners, where on a 390px screen they crowded the
 * wordmark and sat where thumbs do not reach. Same auth logic as the header
 * so the two never disagree.
 */
function MobileActions() {
  const { userId, isLoading } = useUserRole()
  const knownLoggedOut = !isLoading && !userId
  const knownLoggedIn = !isLoading && !!userId
  return (
    <div className="mt-4 flex items-center justify-center gap-2 md:hidden">
      <Link
        href={knownLoggedIn ? "/dashboard" : "/listings"}
        className="inline-flex h-[25px] items-center rounded-full px-3 text-[11px] font-medium text-white shadow-sm transition-opacity hover:opacity-90 active:scale-95"
        style={CHIP_DARK}
      >
        {knownLoggedIn ? "Dashboard" : "Explore Shops"}
      </Link>
      <Button
        asChild
        size="sm"
        className="h-[25px] gap-1 rounded-full bg-white px-3 text-[11px] font-medium text-black shadow-sm transition-opacity hover:bg-white hover:opacity-90 active:scale-95"
      >
        <Link href={knownLoggedOut ? "/auth/signup?role=barber" : "/listings"}>
          {knownLoggedOut ? "Join" : "Explore Shops"}
          {knownLoggedOut && <Plus className="size-3" strokeWidth={2.25} />}
        </Link>
      </Button>
    </div>
  )
}

/**
 * No PublicNavGlass here on purpose — the request was a page that reads
 * like one focused search surface, not a marketing page with a menu. Just
 * the mark (home) and a single auth-aware action, top-right.
 */
function MinimalHeader() {
  const { userId, isLoading } = useUserRole()
  // Signed-out is the only state that gets its own pill (Join). Loading and
  // signed-in render identically — "Explore Shops" either way — so there's
  // nothing to flash: the button never shows Join for a beat and then
  // switches once auth resolves, it only ever becomes Join once we're sure
  // that's actually the right CTA.
  const knownLoggedOut = !isLoading && !userId
  // Mirror image for the left pill. Signed out it points at the shops —
  // someone who landed on /search is already looking for somewhere to work,
  // so the useful next step is the inventory, not the marketing page. Once
  // auth resolves to a user the same pill is the way back to their dashboard
  // (/dashboard routes by role).
  //
  // The two pills never say the same thing: the right one only reads "Explore
  // Shops" for a signed-in visitor, and in that state this one reads
  // "Dashboard".
  const knownLoggedIn = !isLoading && !!userId

  // paddingTop via style, not a Tailwind arbitrary value: those silently fail
  // Plain 1.5rem, no env() — this header is `sticky`, so it sits in normal
  // flow and already starts below the status-bar inset that <body> carries
  // inside the native shell (globals.css, html.native-shell). It used to read
  // max(1.5rem, env(...)), which did the wrong thing twice over: on a notched
  // phone the inset REPLACED the designed gap, so the wordmark sat flush under
  // the clock, and once the body carries the inset it would have been counted
  // twice.
  return (
    <header className="sticky top-0 z-20 flex items-center justify-center px-4 pb-2" style={{ paddingTop: "1.5rem" }}>
      {/* The glass lives on its own layer behind the content, not on the
          header itself. Canvas at 78% over a blur, not solid, so results blur
          through it the same way they blur through the pinned search pill;
          on the empty hero nothing scrolls under it and it is invisible
          against the solid canvas.

          The mask fades the glass out across its bottom half, so the strip
          never ends in a line: with results under it, the blurred 78% strip
          meeting the unblurred cards along one straight edge read as a rule.
          It has to be on this layer and not the header — a mask on the header
          faded the wordmark and the pills with it, and "BOOTH" came out with
          a sheen, white at the top and grey at the baseline. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: searchBgAlpha(0.78),
          backdropFilter: "blur(18px) saturate(140%)",
          WebkitBackdropFilter: "blur(18px) saturate(140%)",
          maskImage: "linear-gradient(to bottom, black 55%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 55%, transparent 100%)",
        }}
      />
      <Link href="/" aria-label="BOOTH home">
        {/* Full wordmark (icon + "BOOTH" text), not just the icon glyph —
            this page is always dark, so the white-text image is the
            correct one regardless of the visitor's device theme. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/images/booth-wordmark-dark.webp" alt="BOOTH" className="h-[16.5px] w-auto object-contain" />
      </Link>
      {/* top-6 / bottom-2 mirror the header padding so both pills centre on
          the wordmark line instead of hanging below it. */}
      <div className="absolute left-4 top-6 bottom-2 hidden items-center md:left-6 md:flex">
        <Link
          href={knownLoggedIn ? "/dashboard" : "/listings"}
          // The dashboard Withdraw pill: dark gradient, no stroke, soft
          // shadow, white medium text. Join beside it is its white twin.
          className="inline-flex h-[25px] items-center rounded-full px-3 text-[11px] font-medium text-white shadow-sm transition-opacity hover:opacity-90 active:scale-95"
          style={CHIP_DARK}
        >
          {knownLoggedIn ? "Dashboard" : "Explore Shops"}
        </Link>
      </div>
      <div className="absolute right-4 top-6 bottom-2 hidden items-center md:right-6 md:flex">
        <Button
          asChild
          size="sm"
          className={cn(
            "h-[25px] gap-1 rounded-full bg-white px-3 text-[11px] font-medium text-black shadow-sm transition-opacity hover:bg-white hover:opacity-90 active:scale-95",
          )}
        >
          <Link href={knownLoggedOut ? "/auth/signup?role=barber" : "/listings"}>
            {knownLoggedOut ? "Join" : "Explore Shops"}
            {knownLoggedOut && <Plus className="size-3" strokeWidth={2.25} />}
          </Link>
        </Button>
      </div>
    </header>
  )
}

export default function WorkSearchPage() {
  const [query, setQuery] = useState("")
  const [listings, setListings] = useState<WorkSearchListing[] | null>(null)
  const [jobsByShop, setJobsByShop] = useState<Map<string, WorkJob[]>>(() => new Map())
  const [facets, setFacets] = useState<WorkFacets>(EMPTY_FACETS)
  const [sort, setSort] = useState<SortKey>("recommended")
  const [loadError, setLoadError] = useState(false)
  const headline = useRotator(HEADLINES.length, BEAT_MS)
  const placeholder = useRotator(PLACEHOLDER_TEXTS.length, BEAT_MS, BEAT_MS / 2)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the field on arrival — but only where focusing costs nothing. The
  // old `autoFocus` attribute fired everywhere, so a phone landed on /search
  // with the keyboard already up, half the results hidden behind it and the
  // page scrolled somewhere nobody asked for. A coarse pointer waits for a
  // tap; a mouse keeps the caret, which is the whole point of landing here.
  useEffect(() => {
    if (typeof window === "undefined") return
    if (window.matchMedia?.("(pointer: coarse)").matches) return
    inputRef.current?.focus()
  }, [])
  // Every tap on this page makes a sound — the search box, the chips, the
  // header pills, the sort menu, the closing CTAs. Synthesized, no assets.
  useUiSounds()

  // Seeded from ?q= so the landing hero's field can hand a city over, and so a
  // search is a link someone can send.
  //
  // In an effect rather than a lazy useState initialiser: this component is
  // server-rendered too, so the initialiser would produce "" on the server and
  // the query on the client — a hydration mismatch on a controlled input. And
  // in an effect rather than useSearchParams(), which would need a Suspense
  // boundary around the whole page to avoid de-opting the route.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q")?.trim()
    if (q) setQuery(q)
  }, [])

  useEffect(() => {
    let cancelled = false
    fetch("/api/shops")
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status))
        return res.json()
      })
      .then((data) => {
        if (!cancelled) setListings(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Open job posts, same visibility rules as /hiring (published, unfilled).
  // Attached by shop so a card can say what the role is, and so
  // "job" / "commission" / "fades" find the shop that posted for it.
  useEffect(() => {
    let cancelled = false
    const supabase = createClient()
    supabase
      .from("job_listings")
      .select("id, shop_id, slug, title, position_type, compensation, specialties, experience_required")
      .eq("is_published", true)
      .is("filled_at", null)
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data }: { data: WorkJob[] | null }) => {
        if (cancelled || !data) return
        const map = new Map<string, WorkJob[]>()
        for (const j of data) {
          const list = map.get(j.shop_id) ?? []
          list.push(j)
          map.set(j.shop_id, list)
        }
        setJobsByShop(map)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const listingsWithJobs = useMemo(
    () => listings?.map((l) => ({ ...l, jobs: jobsByShop.get(l.id) ?? [] })) ?? null,
    [listings, jobsByShop],
  )

  const deferredQuery = useDeferredValue(query)
  const hasFacets = facetsActive(facets)
  // A facet alone is a search — a barber who taps "Chicago" and "Commission"
  // shouldn't also have to type something to see results.
  const hasQuery = deferredQuery.trim().length > 0 || hasFacets
  const parsed = useMemo(() => parseWorkQuery(deferredQuery), [deferredQuery])
  const results = useMemo(() => {
    if (!listingsWithJobs || !hasQuery) return []
    return sortResults(filterShopsForWork(listingsWithJobs, parsed, facets), sort)
  }, [listingsWithJobs, parsed, facets, hasQuery, sort])

  return (
    <div
      data-nav-theme="dark"
      className="relative flex min-h-screen flex-col text-white"
      // One static pool of light, top centre, over the flat canvas: a hint of
      // a surface, not a drift. 3.5% white is as far as it goes before it
      // reads as a gradient again — the animated version was pulled for that.
      style={{ background: `radial-gradient(90% 55% at 50% 0%, rgba(255,255,255,0.035) 0%, transparent 70%), ${SEARCH_BG}` }}
    >
      {/* Grain, over everything including the header and the pills: the one
          texture on an otherwise solid canvas, so the blur reads matte instead
          of plastic — the glass surfaces' tile at the glass surfaces' dark
          strength. `absolute`, never `fixed`: iOS
          Safari samples fixed overlays that touch the viewport edge to paint
          its bars (see components/glass/glass-kit.tsx). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-30 mix-blend-overlay"
        style={{ backgroundImage: GRAIN_URL, opacity: 0.22 }}
      />
      <MinimalHeader />

      <main
        className={cn(
          "flex flex-1 flex-col items-center px-4",
          // Empty state sits 2rem above true centre — dead-centre reads low
          // once the header is on top, and the chips under the pill pull the
          // visual weight down another line. pb-16 (4rem) on a centred flex
          // column moves the centre up by exactly half of it.
          hasQuery ? "justify-start pt-8 md:pt-10" : "justify-center pb-16",
        )}
      >
        <div className={cn("w-full max-w-2xl text-center transition-all duration-500", hasQuery ? "mb-6" : "mb-10")}>
          <h1
            key={headline.index}
            className={cn(
              headline.exiting ? "search-text-out" : "search-text-in",
              "font-light tracking-tight transition-[font-size] duration-500",
              hasQuery ? "text-xl md:text-2xl" : "text-2xl md:text-4xl",
            )}
          >
            {HEADLINES[headline.index]}
          </h1>
        </div>

        <div
          className={cn(
            // 88% on phones (a tenth wider than the 80% desktop share) — the
            // pill read narrow on a phone. The landing hero's copy matches.
            "w-[88%] max-w-[538px] md:w-4/5",
            // Standard sticky-search pattern: once there's a results list to
            // scroll, the bar itself pins just under the header instead of
            // scrolling away with the headline above it. The wrapper stays
            // transparent on purpose — the pill is glass with a backdrop
            // blur, so results blur through it. The old bg-black slab behind
            // it read as a square box cutting across the cards.
            hasQuery && "sticky top-14 z-10 md:top-16",
          )}
        >
          <div className="relative w-full rounded-full">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search by shop name, city, state, country, or zip code"
              // No native placeholder — it can't be animated (it's a browser-
              // native string, not styleable content), so the rotating text
              // below is a real absolutely-positioned element instead.
              placeholder=""
              className="search-pill rounded-full border-0 px-11 text-center text-sm text-white backdrop-blur-xl placeholder:text-neutral-500 focus-visible:ring-0 focus-visible:ring-offset-0 md:text-base"
              style={{
                // From the Figma spec: #080808 fill, #323232 hairline at 0.75
                // inside, full pill. The fill is run at ~70% over a
                // backdrop-blur instead of solid — on the empty hero it reads
                // as the spec's near-black, and once the bar pins and results
                // scroll under it the cards blur through the glass rather than
                // stopping dead at a solid edge. The stroke is an inset shadow
                // (not a border) so it sits inside the pill at 0.75px the way
                // the Figma stroke does; a faint top catch-light stays so the
                // pill still reads as glass, not a flat swatch.
                background: "rgba(8,8,8,0.72)",
                boxShadow: "inset 0 0 0 0.75px #323232, inset 0 1px 0 rgba(255,255,255,0.05)",
                // Holds the pill shape for the backdrop blur: on hover Chrome
                // and Safari can repaint the blur as a square box. Both
                // shadows are inset, so the clip costs nothing.
                clipPath: "inset(0 round 9999px)",
              }}
            />
            {!query && (
              <span
                key={placeholder.index}
                aria-hidden
                className={cn(
                  placeholder.exiting ? "search-text-out search-text-out--soft" : "search-text-in search-text-in--soft",
                  "pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-neutral-500 md:text-base",
                )}
              >
                {PLACEHOLDER_TEXTS[placeholder.index]}
              </span>
            )}
            <button
              type="button"
              aria-label={hasQuery ? "Clear search" : "Search"}
              onClick={() => {
                if (hasQuery) {
                  setQuery("")
                  setFacets(EMPTY_FACETS)
                } else inputRef.current?.focus()
              }}
              // Bare white glyph, no button disc — the pill is the control;
              // a second filled shape inside it was a button inside a button.
              className="absolute right-3 top-1/2 grid size-7 -translate-y-1/2 place-items-center text-white/80 transition-colors hover:text-white active:scale-95"
            >
              {hasQuery ? <X className="size-4" strokeWidth={1.75} /> : <Search className="size-4" strokeWidth={1.75} />}
            </button>
          </div>

          {!hasQuery && (
            // One row, always — four cities stacking 3 + 1 read as a broken
            // grid. A touch tighter on phones so the row fits a 360px screen;
            // justify-center keeps it centred even if it outgrows the pill.
            <div className="mt-4 flex flex-nowrap justify-center gap-1.5 md:gap-2">
              {EXAMPLES.map((example) => (
                <Chip key={example} onClick={() => setQuery(example)} className="shrink-0 whitespace-nowrap px-2.5">
                  {example}
                </Chip>
              ))}
            </div>
          )}
          {!hasQuery && <MobileActions />}
        </div>

        {hasQuery && (
          <div className="mt-8 w-full max-w-4xl animate-in fade-in slide-in-from-bottom-4 pb-24 duration-300">
            {loadError ? (
              <p className="text-center text-neutral-500">
                Couldn&apos;t load listings right now — try again in a moment.
              </p>
            ) : !listings ? (
              <div className="flex items-center justify-center gap-2 py-12 text-neutral-500">
                <Loader2 className="size-4 animate-spin" />
                Loading listings…
              </div>
            ) : (
              <>
                {results.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
                <p className="text-white">No shops match that search yet.</p>
                <p className="mt-1 text-sm text-neutral-500">Try a broader city.</p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <p className="text-sm text-white">
                    {results.length} shop{results.length === 1 ? "" : "s"} hiring
                    {results.some((r) => (r.jobs?.length ?? 0) > 0) && (
                      <span className="text-neutral-500">
                        {" "}
                        · {results.filter((r) => (r.jobs?.length ?? 0) > 0).length} with a posted job
                      </span>
                    )}
                  </p>
                  <SortMenu sort={sort} onChange={setSort} />
                </div>
                <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
                  {results.map((listing) => (
                    <ResultCard key={listing.id} listing={listing} />
                  ))}
                </div>

                {/* Two closes, side by side: the barber looking at these
                    results, and the owner whose shop isn't in them yet. Both
                    are free accounts; the pill matches the header's Join.
                    text-balance evens the lines out so a card never ends on
                    a lone word ("in.", "it."). */}
                <div className="mt-16 grid grid-cols-2 gap-3 py-6">
                  <div className="flex flex-col items-center rounded-[2rem] px-5 py-8 text-center shadow-sm" style={CLOSE_CARD}>
                    <div className="flex-1">
                      {/* The icon glyph, grey like the wordmark's, so the card reads as BOOTH's own close. */}
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src="/images/booth-icon-gray.svg" alt="" aria-hidden className="mx-auto mb-4 h-3.5 w-auto" />
                      <p className="text-balance font-medium text-white">Ready for a booth?</p>
                      <p className="mx-auto mt-2 max-w-[34ch] text-balance text-sm leading-relaxed text-neutral-400">Create a free barber account to message shops and lock one in.</p>
                    </div>
                    <Button
                      asChild
                      className="mt-6 h-8 gap-1 rounded-full bg-white px-4 text-xs font-medium text-black shadow-sm transition-opacity hover:bg-white hover:opacity-90 active:scale-95"
                    >
                      <Link href="/auth/signup?role=barber">
                        Create free account
                        <Plus className="size-3" strokeWidth={2.25} />
                      </Link>
                    </Button>
                  </div>
                  <div className="flex flex-col items-center rounded-[2rem] px-5 py-8 text-center shadow-sm" style={CLOSE_CARD}>
                    <div className="flex-1">
                      {/* The icon glyph, grey like the wordmark's, so the card reads as BOOTH's own close. */}
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src="/images/booth-icon-gray.svg" alt="" aria-hidden className="mx-auto mb-4 h-3.5 w-auto" />
                      <p className="text-balance font-medium text-white">Shop owner?</p>
                      <p className="mx-auto mt-2 max-w-[34ch] text-balance text-sm leading-relaxed text-neutral-400">List your shop for free — barbers searching your city will find it.</p>
                    </div>
                    <Button
                      asChild
                      className="mt-6 h-8 gap-1 rounded-full bg-white px-4 text-xs font-medium text-black shadow-sm transition-opacity hover:bg-white hover:opacity-90 active:scale-95"
                    >
                      <Link href="/auth/signup?role=owner">
                        List your shop
                        <Plus className="size-3" strokeWidth={2.25} />
                      </Link>
                    </Button>
                  </div>
                </div>
              </>
            )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
